import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Users, Gift, Loader2 } from 'lucide-react';
import { api } from '../services/api';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const Title = styled.h1`
  font-size: 1.9rem;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 0.55rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-weight: 600;
  color: #475569;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
`;

const CardTitle = styled.div`
  color: #64748b;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
`;

const CardValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #0f172a;
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChartWrapper = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 18px;
`;

const BarChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 220px;
  padding: 12px 8px 0;
  overflow-x: auto;
`;

const BarGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  min-width: 64px;
`;

const Bar = styled.div<{ $height: number; $color: string }>`
  width: 18px;
  height: ${props => props.$height}%;
  background: ${props => props.$color};
  border-radius: 6px 6px 0 0;
  transition: height 0.2s ease;
`;

const BarLabel = styled.div`
  text-align: center;
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 6px;
`;

const Legend = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #475569;
`;

const LegendSwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${props => props.$color};
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
`;

const ListMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ListTitle = styled.div`
  font-weight: 600;
  color: #0f172a;
`;

const ListSub = styled.div`
  font-size: 0.85rem;
  color: #64748b;
`;

const ListValue = styled.div`
  font-weight: 700;
  color: #0f172a;
`;

const EmptyState = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;
  text-align: center;
  padding: 20px;
`;

interface SummaryData {
  total_customers: number;
  active_customers: number;
  new_customers: number;
  total_points_earned: number;
  total_points_redeemed: number;
  total_redemptions: number;
  balance: number;
  average_ticket: number;
}

interface TrendData {
  period: string;
  label: string;
  earned_points: number;
  redeemed_points: number;
  new_customers: number;
  redemptions: number;
}

interface TopCustomer {
  id: number;
  name: string;
  total_points: number;
  store_id: number;
}

interface TopReward {
  reward_id: number;
  total_redemptions: number;
  reward?: {
    id: number;
    name: string;
    points_required?: number;
    image_path?: string;
  };
}

interface LatestRedemption {
  id: number;
  status: string;
  created_at: string;
  customer?: { id: number; name: string };
  reward?: { id: number; name: string };
}

export const AdvancedReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [topRewards, setTopRewards] = useState<TopReward[]>([]);
  const [latestRedemptions, setLatestRedemptions] = useState<LatestRedemption[]>([]);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReports = async (targetMonths: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/reports/advanced', {
        params: { months: targetMonths },
      });
      if (response.data?.success) {
        setSummary(response.data.data.summary);
        setTrends(response.data.data.trends || []);
        setTopCustomers(response.data.data.top_customers || []);
        setTopRewards(response.data.data.top_rewards || []);
        setLatestRedemptions(response.data.data.latest_redemptions || []);
      } else {
        setError(response.data?.message || 'Nao foi possivel carregar os relatorios.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar relatorios.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports(months);
  }, [months]);

  const pointsMax = useMemo(() => {
    return Math.max(
      0,
      ...trends.map(item => Math.max(item.earned_points, item.redeemed_points))
    );
  }, [trends]);

  const activityMax = useMemo(() => {
    return Math.max(
      0,
      ...trends.map(item => Math.max(item.new_customers, item.redemptions))
    );
  }, [trends]);

  const formatPeriodLabel = (period: string) => {
    const [year, month] = period.split('-').map(Number);
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const monthLabel = months[(month || 1) - 1] || 'Mes';
    return `${monthLabel}/${year}`;
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'issued':
        return 'Emitido';
      case 'used':
        return 'Utilizado';
      case 'cancelled':
        return 'Cancelado';
      case 'expired':
        return 'Expirado';
      default:
        return status || '-';
    }
  };

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={18} />
          Voltar ao dashboard
        </BackButton>
        <Title>
          <BarChart3 size={26} />
          Relatórios Avançados
        </Title>
        <FilterRow>
          <Select value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            <option value={3}>Ultimos 3 meses</option>
            <option value={6}>Ultimos 6 meses</option>
            <option value={12}>Ultimos 12 meses</option>
          </Select>
        </FilterRow>
      </Header>

      {loading && (
        <Card>
          <SectionTitle>
            <Loader2 size={20} />
            Carregando relatorios...
          </SectionTitle>
        </Card>
      )}

      {!loading && error && (
        <Card>
          <EmptyState>{error}</EmptyState>
        </Card>
      )}

      {!loading && !error && summary && (
        <>
          <Grid>
            <Card>
              <CardTitle>Total de clientes</CardTitle>
              <CardValue>{summary.total_customers}</CardValue>
            </Card>
            <Card>
              <CardTitle>Clientes ativos</CardTitle>
              <CardValue>{summary.active_customers}</CardValue>
            </Card>
            <Card>
              <CardTitle>Novos clientes</CardTitle>
              <CardValue>{summary.new_customers}</CardValue>
            </Card>
            <Card>
              <CardTitle>Pontos emitidos</CardTitle>
              <CardValue>{summary.total_points_earned}</CardValue>
            </Card>
            <Card>
              <CardTitle>Pontos resgatados</CardTitle>
              <CardValue>{summary.total_points_redeemed}</CardValue>
            </Card>
            <Card>
              <CardTitle>Saldo de pontos</CardTitle>
              <CardValue>{summary.balance}</CardValue>
            </Card>
            <Card>
              <CardTitle>Ticket médio</CardTitle>
              <CardValue>
                {summary.average_ticket > 0
                  ? `R$ ${summary.average_ticket.toFixed(2).replace('.', ',')}`
                  : '-'}
              </CardValue>
            </Card>
            <Card>
              <CardTitle>Total de resgates</CardTitle>
              <CardValue>{summary.total_redemptions}</CardValue>
            </Card>
          </Grid>

          <Section>
            <SectionHeader>
              <SectionTitle>
                <BarChart3 size={20} />
                Tendencia de pontos
              </SectionTitle>
            </SectionHeader>
            <ChartWrapper>
              {trends.length === 0 || pointsMax === 0 ? (
                <EmptyState>Sem dados no periodo.</EmptyState>
              ) : (
                <>
                  <BarChart>
                    {trends.map(item => (
                      <div key={item.period}>
                        <BarGroup>
                          <Bar
                            $height={Math.max(6, (item.earned_points / pointsMax) * 100)}
                            $color="#00d4aa"
                          />
                          <Bar
                            $height={Math.max(6, (item.redeemed_points / pointsMax) * 100)}
                            $color="#0f172a"
                          />
                        </BarGroup>
                        <BarLabel>{formatPeriodLabel(item.period)}</BarLabel>
                      </div>
                    ))}
                  </BarChart>
                  <Legend>
                    <LegendItem>
                      <LegendSwatch $color="#00d4aa" />
                      Pontos emitidos
                    </LegendItem>
                    <LegendItem>
                      <LegendSwatch $color="#0f172a" />
                      Pontos resgatados
                    </LegendItem>
                  </Legend>
                </>
              )}
            </ChartWrapper>
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>
                <Users size={20} />
                Clientes e resgates
              </SectionTitle>
            </SectionHeader>
            <ChartWrapper>
              {trends.length === 0 || activityMax === 0 ? (
                <EmptyState>Sem dados no periodo.</EmptyState>
              ) : (
                <>
                  <BarChart>
                    {trends.map(item => (
                      <div key={`${item.period}-activity`}>
                        <BarGroup>
                          <Bar
                            $height={Math.max(6, (item.new_customers / activityMax) * 100)}
                            $color="#f97316"
                          />
                          <Bar
                            $height={Math.max(6, (item.redemptions / activityMax) * 100)}
                            $color="#6366f1"
                          />
                        </BarGroup>
                        <BarLabel>{formatPeriodLabel(item.period)}</BarLabel>
                      </div>
                    ))}
                  </BarChart>
                  <Legend>
                    <LegendItem>
                      <LegendSwatch $color="#f97316" />
                      Novos clientes
                    </LegendItem>
                    <LegendItem>
                      <LegendSwatch $color="#6366f1" />
                      Resgates
                    </LegendItem>
                  </Legend>
                </>
              )}
            </ChartWrapper>
          </Section>

          <TwoColumn>
            <Card>
              <SectionHeader>
                <SectionTitle>
                  <Users size={18} />
                  Clientes com mais pontos
                </SectionTitle>
              </SectionHeader>
              {topCustomers.length === 0 ? (
                <EmptyState>Sem dados.</EmptyState>
              ) : (
                <List>
                  {topCustomers.map(item => (
                    <ListItem key={item.id}>
                      <ListMeta>
                        <ListTitle>{item.name}</ListTitle>
                        <ListSub>Loja #{item.store_id}</ListSub>
                      </ListMeta>
                      <ListValue>{item.total_points} pts</ListValue>
                    </ListItem>
                  ))}
                </List>
              )}
            </Card>

            <Card>
              <SectionHeader>
                <SectionTitle>
                  <Gift size={18} />
                  Recompensas mais resgatadas
                </SectionTitle>
              </SectionHeader>
              {topRewards.length === 0 ? (
                <EmptyState>Sem dados.</EmptyState>
              ) : (
                <List>
                  {topRewards.map(item => (
                    <ListItem key={item.reward_id}>
                      <ListMeta>
                        <ListTitle>{item.reward?.name || 'Recompensa'}</ListTitle>
                        <ListSub>{item.reward?.points_required ? `${item.reward.points_required} pts` : '-'}</ListSub>
                      </ListMeta>
                      <ListValue>{item.total_redemptions} resgates</ListValue>
                    </ListItem>
                  ))}
                </List>
              )}
            </Card>
          </TwoColumn>

          <Section>
            <SectionHeader>
              <SectionTitle>
                <Gift size={18} />
                Ultimos resgates
              </SectionTitle>
            </SectionHeader>
            <ChartWrapper>
              {latestRedemptions.length === 0 ? (
                <EmptyState>Sem resgates recentes.</EmptyState>
              ) : (
                <List>
                  {latestRedemptions.map(item => (
                    <ListItem key={item.id}>
                      <ListMeta>
                        <ListTitle>{item.reward?.name || 'Recompensa'}</ListTitle>
                        <ListSub>{item.customer?.name || 'Cliente'} • {new Date(item.created_at).toLocaleDateString()}</ListSub>
                      </ListMeta>
                      <ListValue>{getStatusLabel(item.status)}</ListValue>
                    </ListItem>
                  ))}
                </List>
              )}
            </ChartWrapper>
          </Section>
        </>
      )}
    </PageContainer>
  );
};
